import * as React from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { Provider } from 'mobx-react';
import { StaticRouter } from 'react-router-dom';
const html2pdf = require('html2pdf.js');

const delay = (ms: number = 0): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

export interface ExportToPDFOptions {
    margin?: number;
    filename?: string;
    image?: { type: string, quality: number };
    html2canvas?: { scale: number, useCORS: boolean, allowTaint: boolean };
    jsPDF?: { unit: string, format: string, orientation: string };
    pagebreak?: { mode: string | string[], before?: string[], after?: string[], avoid?: string[] };
}

export const CreateOptions = (scale:number = 2, quality:number = .8) => {
    return {
        margin: 0,
        filename: '',
        image: { type: 'jpeg', quality: quality },
        html2canvas: { scale: scale, useCORS: true, allowTaint: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
}

const buildPdfWorker = (
    pages: React.ReactNode | React.ReactNode[],
    fileName: string,
    optionsOverride?: Partial<ExportToPDFOptions>,
    batchSize: number = 16
) => {
    const defaultOptions = CreateOptions();
    defaultOptions.filename = fileName;

    const options = { ...defaultOptions, ...optionsOverride };

    const pageArray = Array.isArray(pages) ? pages : [pages];

    let worker = html2pdf().set(options);

    // Process pages in batches to reduce DOM cloning overhead
    for (let batchStart = 0; batchStart < pageArray.length; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, pageArray.length);
        const batch = pageArray.slice(batchStart, batchEnd);

        // Render batch together
        const batchHtml = ReactDOMServer.renderToStaticMarkup(
            React.createElement(
                Provider,
                {
                    FilterStore: {},
                    UserStore: {},
                    OrganizationStore: {},
                    ServiceStore: {},
                    EnumStore: {}
                },
                React.createElement(StaticRouter, { context: {} },
                    React.createElement(React.Fragment, null, batch)
                )
            )
        );

        if (batchStart === 0) {
            worker = worker.from(batchHtml).toPdf();
        } else {
            worker = worker.get('pdf').then((pdf: any) => {
                pdf.addPage();
            }).from(batchHtml).toContainer().toCanvas().toImg().toPdf();
        }
    }

    return worker;
};

export const exportToPDF = async (
    pages: React.ReactNode | React.ReactNode[],
    fileName: string,
    optionsOverride?: Partial<ExportToPDFOptions>,
    batchSize: number = 16
): Promise<void> => {
    try {
        const worker = buildPdfWorker(pages, fileName, optionsOverride, batchSize);
        await worker.save();
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    }
};

// Same as exportToPDF, but returns the generated PDF as a Blob instead of triggering a download.
// Used when the PDF needs to be uploaded (e.g. emailed as an attachment) rather than saved locally.
export const exportToPDFBlob = async (
    pages: React.ReactNode | React.ReactNode[],
    fileName: string,
    optionsOverride?: Partial<ExportToPDFOptions>,
    batchSize: number = 16
): Promise<Blob> => {
    try {
        const worker = buildPdfWorker(pages, fileName, optionsOverride, batchSize);
        return await worker.outputPdf('blob');
    } catch (error) {
        console.error('Error generating PDF blob:', error);
        throw error;
    }
};



// WEBPACK FOOTER //
// ./src/utilities/Pdf.ts